import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { useProject } from "@/components/AppShell";
import { DownloadMdButton } from "@/components/DownloadMdButton";
import { buildCompetitorsMd } from "@/lib/mdBuilders";
import { MetricInfo } from "@/components/MetricInfo";
import { CreditActionButton } from "@/components/CreditActionButton";
import { Users, RefreshCw, Compass } from "lucide-react";

function MarketExplorerPanel() {
    const [domain, setDomain] = useState("");
    const [data, setData] = useState(null);
    const [checking, setChecking] = useState(false);

    const check = async () => {
        const d = domain.trim();
        if (!d) { toast.error("Enter a domain to explore."); return; }
        setChecking(true);
        try {
            const r = await client.get("/market-explorer", { params: { domain: d } });
            setData(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Lookup failed");
        } finally {
            setChecking(false);
        }
    };

    const refresh = async () => {
        const d = domain.trim();
        if (!d) { toast.error("Enter a domain to explore."); return; }
        try {
            const r = await client.post("/market-explorer", { domain: d });
            setData(r.data);
            toast.success(`Pulled market data for ${d}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Refresh failed");
        }
    };

    return (
        <div className="space-y-4" data-testid="market-explorer-panel">
            <p className="text-sm text-muted-foreground">Size up any domain — not just your tracked competitors — before pitching a new client.</p>
            <div className="flex items-center gap-2 flex-wrap">
                <input
                    data-testid="market-explorer-domain-input"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && check()}
                    placeholder="Enter any domain, e.g. competitor.com"
                    className="bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary flex-1 min-w-[260px]"
                />
                <button
                    data-testid="market-explorer-lookup-btn"
                    onClick={check}
                    disabled={checking}
                    className="border border-border px-4 py-2 text-sm font-mono hover:border-primary hover:text-primary disabled:opacity-50"
                >
                    {checking ? "Checking…" : "Check"}
                </button>
            </div>

            {data && data.available === false && (
                <div className="border border-border bg-card/40 p-8 text-center" data-testid="market-explorer-empty">
                    <Compass className="w-7 h-7 mx-auto text-muted-foreground mb-3" strokeWidth={1.5} />
                    <div className="text-sm text-muted-foreground mb-4">
                        {data.reason === "not_configured"
                            ? "Market Explorer needs a live DataForSEO connection."
                            : `No cached data yet for "${data.domain}".`}
                    </div>
                    {data.reason === "not_cached" && (
                        <CreditActionButton
                            actionKey="market_explorer"
                            testid="pull-market-data-btn"
                            onConfirm={refresh}
                            description="Pulls a real domain rank overview + top 20 organic keywords from DataForSEO Labs for this domain."
                        >
                            <span className="inline-flex items-center gap-1"><RefreshCw className="w-4 h-4" /> Pull market data</span>
                        </CreditActionButton>
                    )}
                </div>
            )}

            {data && data.available && (
                <div className="space-y-4" data-testid="market-explorer-results">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="font-display text-xl font-mono">{data.domain}</div>
                        <CreditActionButton
                            actionKey="market_explorer"
                            testid="refresh-market-data-btn"
                            variant="outline"
                            onConfirm={refresh}
                            description="Pulls a fresh domain rank overview + top organic keywords from DataForSEO Labs."
                        >
                            <span className="inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Refresh</span>
                        </CreditActionButton>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm font-mono">
                        <div className="border border-border p-3">
                            <div className="text-muted-foreground text-xs">Organic keywords</div>
                            <div className="text-lg mt-1">{(data.overview.keywords || 0).toLocaleString()}</div>
                        </div>
                        <div className="border border-border p-3">
                            <div className="text-muted-foreground text-xs">Est. traffic</div>
                            <div className="text-lg mt-1">{(data.overview.traffic || 0).toLocaleString()}</div>
                        </div>
                        <div className="border border-border p-3">
                            <div className="text-muted-foreground text-xs">Top 10 rankings</div>
                            <div className="text-lg mt-1">{(data.overview.top10 || 0).toLocaleString()}</div>
                        </div>
                    </div>
                    <div className="border border-border overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs text-muted-foreground">
                                <tr className="border-b border-border">
                                    <th className="text-left p-3">Keyword</th>
                                    <th className="text-right p-3">Volume</th>
                                    <th className="text-right p-3">Difficulty</th>
                                    <th className="text-right p-3">Position</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data.top_keywords || []).map((k, i) => (
                                    <tr key={i} className="border-b border-border/50 hover:bg-accent/20">
                                        <td className="p-3 font-mono">{k.keyword}</td>
                                        <td className="p-3 text-right font-mono text-muted-foreground">{(k.volume || 0).toLocaleString()}</td>
                                        <td className="p-3 text-right font-mono">{k.difficulty ?? "—"}</td>
                                        <td className="p-3 text-right font-mono text-success">#{k.position ?? "—"}</td>
                                    </tr>
                                ))}
                                {(!data.top_keywords || data.top_keywords.length === 0) && (
                                    <tr><td colSpan={4} className="p-6 text-center text-muted-foreground text-sm">No keyword data returned.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function Competitors() {
    const project = useProject();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("competitors"); // "competitors" | "market"

    const load = () => {
        if (!project) return;
        client.get(`/projects/${project.id}/competitors`).then((r) => setData(r.data));
    };

    useEffect(load, [project]);

    const refresh = async () => {
        try {
            const r = await client.post(`/projects/${project.id}/competitors/refresh`);
            setData(r.data);
            toast.success("Competitors refreshed from DataForSEO Labs");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Refresh failed");
        }
    };

    if (!project || !data) return <div className="text-muted-foreground font-mono text-sm">loading competitors…</div>;

    return (
        <div className="space-y-6" data-testid="competitors-page">
            <div>
                <div className="overline">Competitive intelligence</div>
                <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-2">Competitors</h1>
                <p className="text-sm text-muted-foreground mt-2">Share of voice, gap analysis, and beat-them-here opportunities.</p>
            </div>

            <div className="flex items-center gap-1 border border-border w-fit p-0.5" role="tablist" data-testid="competitors-view-tabs">
                <button
                    data-testid="tab-my-competitors"
                    role="tab"
                    aria-selected={tab === "competitors"}
                    onClick={() => setTab("competitors")}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${tab === "competitors" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                    My Competitors
                </button>
                <button
                    data-testid="tab-market-explorer"
                    role="tab"
                    aria-selected={tab === "market"}
                    onClick={() => setTab("market")}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 ${tab === "market" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                    <Compass className="w-3 h-3" /> Market Explorer
                </button>
            </div>

            {tab === "market" && <MarketExplorerPanel />}

            {tab === "competitors" && data.available === false && (
                <div className="border border-border bg-card/40 p-10 text-center" data-testid="competitors-empty-state">
                    <Users className="w-8 h-8 mx-auto text-muted-foreground mb-4" strokeWidth={1.5} />
                    <div className="font-display text-2xl">
                        {data.reason === "not_configured" && "DataForSEO isn't connected yet"}
                        {data.reason === "no_competitors" && "No competitor domains added"}
                        {data.reason === "not_cached" && "No competitor data pulled yet"}
                    </div>
                    <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto">
                        {data.reason === "not_configured" && "Competitor comparison needs a live DataForSEO connection configured by your admin."}
                        {data.reason === "no_competitors" && (data.hint || "Add competitor domains via the edit-project (pencil) button in the top bar to compare.")}
                        {data.reason === "not_cached" && "Pull a real DataForSEO Labs comparison (rankings, traffic, backlinks, keyword gaps) for this project's competitors."}
                    </p>
                    {data.reason === "not_cached" && (
                        <div className="mt-6 flex justify-center">
                            <CreditActionButton
                                actionKey="competitors_refresh"
                                testid="refresh-competitors-btn-empty"
                                onConfirm={refresh}
                                description="Pulls domain rank overview, backlinks, and keyword-gap data from DataForSEO Labs for you and each competitor."
                            >
                                <span className="inline-flex items-center gap-1">
                                    <RefreshCw className="w-4 h-4" /> Pull competitor data
                                </span>
                            </CreditActionButton>
                        </div>
                    )}
                </div>
            )}

            {tab === "competitors" && data.available !== false && (
                <>
                    <div className="flex items-start justify-end gap-4 flex-wrap">
                        <div className="flex flex-col items-end gap-2">
                            <DownloadMdButton
                                filenamePrefix={`inphase-competitors-${project?.domain || "project"}`}
                                buildMarkdown={() => buildCompetitorsMd({ project, data })}
                                disabled={!data}
                                testId="download-competitors-md"
                            />
                            <CreditActionButton
                                actionKey="competitors_refresh"
                                testid="refresh-competitors-btn"
                                variant="outline"
                                onConfirm={refresh}
                                description="Pulls a fresh domain rank overview, backlinks, and keyword-gap comparison from DataForSEO Labs."
                            >
                                <span className="inline-flex items-center gap-1">
                                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                                </span>
                            </CreditActionButton>
                        </div>
                    </div>

                    <div className="border border-border bg-card/40 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs text-muted-foreground">
                                <tr className="border-b border-border">
                                    <th className="text-left p-3">Domain</th>
                                    <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end">Visibility <MetricInfo metricKey="visibility_index" /></span></th>
                                    <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end">Keywords <MetricInfo metricKey="tracked_keywords" /></span></th>
                                    <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end">Traffic <MetricInfo metricKey="estimated_traffic" /></span></th>
                                    <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end">Backlinks <MetricInfo metricKey="total_backlinks" /></span></th>
                                    <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end">DR <MetricInfo metricKey="domain_rating" /></span></th>
                                    <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end">Shared <MetricInfo metricKey="shared_keywords" /></span></th>
                                    <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end">Gap <MetricInfo metricKey="gap_keywords" /></span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.rows.map((r, i) => (
                                    <tr key={i} className={`border-b border-border/50 hover:bg-accent/20 ${r.you ? "bg-primary/5" : ""}`}>
                                        <td className="p-3 font-mono">
                                            {r.domain}
                                            {r.you && <span className="ml-2 text-xs text-primary border border-primary/60 px-1.5 py-0.5">YOU</span>}
                                        </td>
                                        <td className="p-3 text-right font-mono">{r.visibility}%</td>
                                        <td className="p-3 text-right font-mono text-muted-foreground">{r.keywords.toLocaleString()}</td>
                                        <td className="p-3 text-right font-mono text-muted-foreground">{r.traffic.toLocaleString()}</td>
                                        <td className="p-3 text-right font-mono text-muted-foreground">{r.backlinks.toLocaleString()}</td>
                                        <td className="p-3 text-right font-mono">{r.dr}</td>
                                        <td className="p-3 text-right font-mono">{r.shared_keywords}</td>
                                        <td className="p-3 text-right font-mono text-warning">{r.gap_keywords}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="border border-border bg-card/40">
                        <div className="p-4 border-b border-border">
                            <div className="overline">Keyword Gap · sample</div>
                            <div className="text-xs text-muted-foreground mt-1">Keywords competitors rank for that you don't</div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-xs text-muted-foreground">
                                    <tr className="border-b border-border">
                                        <th className="text-left p-3">Keyword</th>
                                        <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end">Volume <MetricInfo metricKey="search_volume" /></span></th>
                                        <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end">KD <MetricInfo metricKey="keyword_difficulty" /></span></th>
                                        <th className="text-right p-3">Your pos.</th>
                                        <th className="text-left p-3">Top competitor</th>
                                        <th className="text-right p-3">Their pos.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.gap.map((g, i) => (
                                        <tr key={i} className="border-b border-border/50 hover:bg-accent/20">
                                            <td className="p-3 font-mono">{g.keyword}</td>
                                            <td className="p-3 text-right font-mono text-muted-foreground">{g.volume.toLocaleString()}</td>
                                            <td className="p-3 text-right font-mono">{g.difficulty}</td>
                                            <td className="p-3 text-right font-mono text-destructive">{g.you_position}</td>
                                            <td className="p-3 text-xs">{g.top_competitor}</td>
                                            <td className="p-3 text-right font-mono text-success">{g.top_competitor_position}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
